// Bot decisions: whom to hit, when a fight is lost and it's time to fall back,
// when to go help the teammate, and when a skill is worth its cooldown. Game.think() runs botThink every
// 0.25 s per bot; with no fight to take, botGoals.ts picks the point, the monster or the trip home. Same code
// online, offline and in scripts/balance.ts, and deterministic: the only randomness is g.rng.
import { LAYOUT } from './layout.js';
import { SKILLS, castSkill, aimCharge, releaseCharge } from './skills.js';
// Only read inside function bodies — safe across the game.ts ↔ botBrain.ts import cycle.
import { RULES } from './game.js';
import { botArtifacts, botShouldShop, botShop, maxHpOf } from './artifacts.js';
import { botRelic, botRelicChoice, relicOf } from './relics.js';
import {
  GOALS, choosePoint, captureFinishing, pointThreatened, hazardsFor, inHazard, hazardExit, safeGround, approachCrosses,
  monsterToHunt, huntStillOn, type Hazard,
} from './botGoals.js';
import { isHero, type Monster, type Target } from './monsters.js';
import { golemOf, summonVisibleTo, type Summon } from './summons.js';
import { bindPlan } from './bind.js';
import type { Game, Hero, BotState } from './game.js';
import type { Role } from './heroes.js';
import type { Team } from './layout.js';
import type { SkillKey } from './commands.js';
import type { Vec2 } from './events.js';


export const BRAIN = {
  aggro: { melee: 8, ranged: 10 },
  stickFor: 2,           // s a freshly picked target keeps its bonus — no flip-flopping between two close scores
  fightRadius: 10,       // who counts toward a local fight
  disengageBelow: 0.45,  // our/their fight power under which an outnumbered bot falls back…
  hopeless: 0.2,         // …or any bot at all
  reengageAbove: 0.8,    // …and over which it comes back, once it has held the retreat this long:
  disengageHold: 1.5,
  caught: 1.5,           // m past an enemy's own reach within which running is pointless: fight instead
  mateReach: 25,         // m within which falling back means joining the teammate rather than going home
  helpRadius: 8,          // how near a teammate under attack must be to pull a bot with nothing of its own to hit
  hitWindow: 1.5,        // "being hit" = took damage this recently
  baseKeepOut: 6,        // m past the enemy fountain's radius no target is chased into
  skillSkip: 0.1,        // a castable, worthwhile skill still waits a think this often — variety, not a rule
};

// Тень's Без отражения (a silhouette after PASSIVES.shadow.stillFor s still: untouchable, holds a point, never takes one).
export const SHADOW = {
  stirEvery: 1.2,  // s still on a point still to be taken before she steps across it — well short of stillFor
  stirStep: 1.5,   // m from the point's centre, on the far side from where she stands
  coverHp: 0.5,    // being hit under this hp share, she stays still after all: the silhouette's safety comes first
};

const ROLE_PRIORITY: Record<Role, number> = { shooter: 12, support: 12, summoner: 10, gamechanger: 8, duelist: 4, guard: 0 };

const flatDist = (a: { x: number; z: number }, b: { x: number; z: number }) => Math.hypot(a.x - b.x, a.z - b.z);
const hpShare = (h: Hero) => h.hp / maxHpOf(h);
const ehp = (g: Game, h: Hero) => h.hp + (h.shield && h.shield.until > g.t ? h.shield.amount : 0);
const aggroOf = (h: Hero) => (h.def.melee ? BRAIN.aggro.melee : BRAIN.aggro.ranged);
export const hitRecently = (g: Game, h: Hero, within = BRAIN.hitWindow) => g.t - h.lastDamageAt < within;
const visibleEnemies = (g: Game, h: Hero) => g.heroes.filter(e => e.team !== h.team && e.alive && g.visibleTo(h.team, e));
// Still in the fight's numbers, but no one to aim at: a silhouette (Без отражения) takes no hit.
const hittableEnemies = (g: Game, h: Hero) => visibleEnemies(g, h).filter(e => !g.silhouette(e));
const mateOf = (g: Game, h: Hero) => g.heroes.find(a => a !== h && a.team === h.team && a.alive);
const attacking = (e: Hero): Target | null => (e.order?.type === 'attack' ? e.order.target : null);

/** Basic swings of `h` it takes to finish `e` (shield included). */
export function hitsToKill(g: Game, h: Hero, e: Hero) {
  return ehp(g, e) / (h.def.dmg * Math.max(1, g.buffVal(h, 'dmg')));
}

export function nearEnemyFountain(team: Team, p: { x: number; z: number }) {
  const [x, z] = LAYOUT.bases[team === 'a' ? 'b' : 'a'];
  return Math.hypot(p.x - x, p.z - z) < RULES.fountainRadius + BRAIN.baseKeepOut;
}

/** The enemy hero a teammate is attacking right now, if any. */
export function allyFocus(g: Game, h: Hero): Hero | null {
  for (const a of g.heroes) {
    if (a === h || a.team !== h.team || !a.alive) continue;
    const t = attacking(a);
    if (t && isHero(t) && t.alive) return t;
  }
  return null;
}

/* Higher = better to hit: wounded, a few swings from dead, a squishy backliner, near, the teammate's pick,
   the one beating on our teammate, the one already picked (for a short while); invulnerable ones last. */
export function scoreTarget(g: Game, h: Hero, e: Hero, focus: Hero | null, sticky: Hero | null): number {
  let s = (1 - hpShare(e)) * 40 - flatDist(h.pos, e.pos) * 2.5 + ROLE_PRIORITY[e.def.role];
  const hits = hitsToKill(g, h, e);
  if (hits <= 2) s += 30; else if (hits <= 4) s += 12;
  if (e === focus) s += 20;
  if (e === sticky) s += 15;
  const victim = attacking(e);
  if (victim && isHero(victim) && victim !== h && victim.team === h.team && hitRecently(g, victim)) s += 15;
  if (g.t < e.invulnUntil || g.protectedBy(e)) s -= 25;
  return s;
}

/** Best visible enemy hero in reach, or null; remembers the pick in `bot` for stickiness. */
export function pickTarget(g: Game, h: Hero, bot: BotState): Hero | null {
  const aggro = aggroOf(h), focus = allyFocus(g, h);
  const sticky = bot.focus != null && g.t < (bot.focusUntil ?? 0) ? g.heroes[bot.focus] ?? null : null;
  let best: Hero | null = null, bestScore = -Infinity;
  for (const e of hittableEnemies(g, h)) {
    if (flatDist(e.pos, h.pos) >= aggro || nearEnemyFountain(h.team, e.pos)) continue;
    const s = scoreTarget(g, h, e, focus, sticky);
    if (s > bestScore) { best = e; bestScore = s; }
  }
  if (!best) bot.focus = null;
  else if (best.id !== bot.focus) { bot.focus = best.id; bot.focusUntil = g.t + BRAIN.stickFor; }
  return best;
}

/* Lanchester-style: a side's power is its summed effective hp times its summed damage rate, ready skills
   counting a little extra. Monsters never count. Infinity = no enemy in the fight at all. */
function power(g: Game, side: Hero[]) {
  let hp = 0, dps = 0;
  for (const x of side) {
    hp += ehp(g, x);
    dps += x.def.dmg / g.attackCdOf(x) * (1 + 0.15 * ((x.cd.Q <= 0 ? 1 : 0) + (x.cd.W <= 0 ? 1 : 0)));
  }
  return hp * dps;
}
export type FightOdds = { odds: number; outnumbered: boolean };
export function fightOdds(g: Game, h: Hero): FightOdds {
  const R = BRAIN.fightRadius;
  const theirs = visibleEnemies(g, h).filter(e => flatDist(e.pos, h.pos) < R);
  if (!theirs.length) return { odds: Infinity, outnumbered: false };
  const ours = g.heroes.filter(a => a.team === h.team && a.alive && flatDist(a.pos, h.pos) < R);
  return { odds: power(g, ours) / power(g, theirs), outnumbered: theirs.length > ours.length };
}

/* Falls back when outnumbered and under `disengageBelow`, or under `hopeless` regardless; comes back, once
   the retreat has lasted `disengageHold` s, over `reengageAbove` — or over `disengageBelow` when no longer
   outnumbered (the teammate got there). A kill about to land is never abandoned.
   An even fight that merely goes badly is fought out: leaving the point costs more than the hp (the low-hp
   run home still applies). */
export function updateDisengage(g: Game, bot: BotState, { odds, outnumbered }: FightOdds, finishing: boolean): boolean {
  if (bot.disengaged) {
    const better = odds >= BRAIN.reengageAbove || (!outnumbered && odds >= BRAIN.disengageBelow);
    if (g.t - (bot.disengagedAt ?? 0) >= BRAIN.disengageHold && better) bot.disengaged = false;
  } else if (!finishing && (odds < BRAIN.hopeless || (outnumbered && odds < BRAIN.disengageBelow))) {
    bot.disengaged = true;
    bot.disengagedAt = g.t;
  }
  return !!bot.disengaged;
}

/** A teammate within reach who is being hit right now. */
export function allyInTrouble(g: Game, h: Hero): Hero | null {
  const a = mateOf(g, h);
  return a && flatDist(a.pos, h.pos) < BRAIN.helpRadius && hitRecently(g, a) ? a : null;
}

/** The visible enemy hero nearest `ally` (the one hitting it, as far as a bot can tell). */
export function attackerOf(g: Game, h: Hero, ally: Hero): Hero | null {
  let best: Hero | null = null, d = 9;
  for (const e of hittableEnemies(g, h)) {
    const de = flatDist(e.pos, ally.pos);
    if (de < d && !nearEnemyFountain(h.team, e.pos)) { best = e; d = de; }
  }
  return best;
}

function moveTo(g: Game, h: Hero, x: number, z: number) {
  const end = h.path[h.path.length - 1];
  if (h.order?.type !== 'move' || !end || Math.hypot(end.x - x, end.z - z) > 2) g.orderMove(h, x, z);
}

/* Out of a lost fight: to the teammate when it stands further from the enemy (the fight evens up there), and
   once beside it, stick with it rather than walk on home; with no teammate to fall back on, home. */
function fallBack(g: Game, h: Hero) {
  const [bx, bz] = LAYOUT.bases[h.team];
  const mate = mateOf(g, h), foe = visibleEnemies(g, h).sort((x, y) => flatDist(x.pos, h.pos) - flatDist(y.pos, h.pos))[0];
  const dm = mate ? flatDist(mate.pos, h.pos) : Infinity;
  const usable = !!mate && !mate.bot?.retreat && hpShare(mate) > 0.4 && dm < BRAIN.mateReach;
  if (usable && dm <= 3) { if (h.order?.type !== 'follow' || h.order.target !== mate) g.orderFollow(h, mate!); }
  else if (usable && (!foe || flatDist(mate!.pos, foe.pos) > flatDist(h.pos, foe.pos))) moveTo(g, h, mate!.pos.x, mate!.pos.z);
  else moveTo(g, h, bx, bz);
}

/* A low-hp bot stays put a moment longer for a capture about to land or a fight it is clearly winning, and
   heals where it stands with Сердце Гидры unless something is still hitting it. */
function holdOn(g: Game, h: Hero): boolean {
  if (hpShare(h) < 0.15) return false;
  if (relicOf(g, h.team) === 'hydraHeart' && !hitRecently(g, h, 3)) return true;
  const { odds } = fightOdds(g, h);
  return captureFinishing(g, h, 1.5) || (odds !== Infinity && odds >= 1.5);
}

export function botThink(g: Game, h: Hero, bot: BotState) {
  const [bx, bz] = LAYOUT.bases[h.team];
  if (hpShare(h) < 0.3 && !bot.retreat && !holdOn(g, h)) bot.retreat = true;
  if (h.kind === 'shadow') {
    if (shadowHoldsStill(g, h, bot)) { if (h.order || h.path.length) g.stop(h); return; }
    if (g.shadowThink(h, bot)) return;
  }
  const aggro = aggroOf(h);
  // An enemy in its own fountain is no one to cast at either (a charge into the fountain, then a U-turn).
  const foes = hittableEnemies(g, h).filter(e => flatDist(e.pos, h.pos) < aggro && !nearEnemyFountain(h.team, e.pos))
    .sort((x, y) => flatDist(x.pos, h.pos) - flatDist(y.pos, h.pos));
  if (bot.retreat) {
    if (hpShare(h) > 0.9) bot.retreat = false;
    else {
      botSkills(g, h, foes[0], true);  // escapes on the way out
      botArtifacts(g, h, foes[0]);
      if (h.state === 'normal' && h.order?.type !== 'move') g.orderMove(h, bx, bz);
      return;
    }
  }
  const target = pickTarget(g, h, bot);
  const finishing = !!target && hitsToKill(g, h, target) <= 2 && flatDist(h.pos, target.pos) < g.attackRangeOf(h) + 1.5;
  // A lost fight is left only while it can still be left: with an enemy already on us, running just means
  // being hit in the back.
  const caught = visibleEnemies(g, h).some(e => flatDist(e.pos, h.pos) < e.def.range + BRAIN.caught);
  const leaving = updateDisengage(g, bot, fightOdds(g, h), finishing) && !caught;
  const foe = target ?? foes[0];
  botRelic(g, h, foe);  // first: a Щит for a teammate about to die outranks the bot's own next skill
  botSkills(g, h, foe, leaving);
  botArtifacts(g, h, foe);
  if (h.state !== 'normal') return;
  if (leaving) { bot.shopping = false; fallBack(g, h); return; }
  // Out of enemy ground, unless a kill or a capture is about to land where we stand. A target that can only be
  // reached through it (or, for melee, stands in it) is left to the skills: the bot carries on with what else
  // there is to do — the teammate, the point — from safe ground.
  const hz = hazardsFor(g, h);
  const killing = !!target && hitsToKill(g, h, target) <= 1 && flatDist(h.pos, target.pos) <= g.attackRangeOf(h) + 0.5;
  if (hz.length && inHazard(hz, h.pos) && !killing && !captureFinishing(g, h, 1)) {
    const out = hazardExit(g, hz, h.pos);
    if (out) { moveTo(g, h, out.x, out.z); return; }
  }
  const reachable = (t: Hero) => killing || !hz.length
    // Already in reach: hit it, wherever it stands. The plain range, not attackRangeOf, for walking there:
    // Сошка's standing bonus is gone the moment the bot walks.
    || flatDist(h.pos, t.pos) <= g.attackRangeOf(h)
    || !(approachCrosses(g, hz, h.pos, t.pos, h.def.range) || (h.def.melee && inHazard(hz, t.pos)));
  // An enemy golem on a point contests it (blocks our capture, holds theirs): it goes before a hero that isn't nearer.
  const golem = golemOnPoint(g, h, aggro);
  if (golem && (!target || flatDist(golem.pos, h.pos) < flatDist(target.pos, h.pos))) {
    bot.shopping = false;
    if (h.order?.type !== 'attack' || h.order.target !== golem) g.orderAttack(h, golem);
    return;
  }
  if (target && reachable(target)) {
    bot.shopping = false;
    if (h.order?.type !== 'attack' || h.order.target !== target) g.orderAttack(h, target);
    return;
  }
  // Help a teammate an enemy hero is hitting — unless that means stepping off a point still being taken.
  const ally = allyInTrouble(g, h), on = ally && attackerOf(g, h, ally);
  if (on && reachable(on) && !g.points.some(p => p.owner !== h.team && flatDist(p.pos, h.pos) < LAYOUT.pointRadius)) {
    bot.shopping = false;
    if (h.order?.type !== 'attack' || h.order.target !== on) g.orderAttack(h, on);
    return;
  }
  // Any other enemy summon only with no hero to hit.
  const pest = summonToHit(g, h, aggro);
  if (pest) {
    if (h.order?.type !== 'attack' || h.order.target !== pest) g.orderAttack(h, pest);
    return;
  }
  // Bots never hunt monsters: they only hit back, with basic attacks, at one that is already after them.
  const threat = g.monsters.find(m => m.alive && m.target === h && m.state !== 'return' && flatDist(m.pos, h.pos) < aggro);
  if (threat) {
    if (h.order?.type !== 'attack' || h.order.target !== threat) g.orderAttack(h, threat);
    return;
  }
  idle(g, h, bot, foes, hz);
}

/* ---------------- enemy summons ---------------- */

const enemySummons = (g: Game, h: Hero, aggro: number) => g.summons.filter(s => s.alive && s.team !== h.team && summonVisibleTo(g, h.team, s)
  && flatDist(s.pos, h.pos) < aggro && !nearEnemyFountain(h.team, s.pos));

/** The nearest enemy golem standing on a capture point. */
function golemOnPoint(g: Game, h: Hero, aggro: number): Summon | null {
  return enemySummons(g, h, aggro).filter(s => s.kind === 'golem' && g.points.some(p => flatDist(p.pos, s.pos) < LAYOUT.pointRadius))
    .sort((x, y) => flatDist(x.pos, h.pos) - flatDist(y.pos, h.pos))[0] ?? null;
}

/** The nearest enemy summon worth a swing — a melee bot never chases a bird it can't already reach. */
function summonToHit(g: Game, h: Hero, aggro: number): Summon | null {
  return enemySummons(g, h, aggro).filter(s => !(s.kind === 'magpie' && h.def.melee && flatDist(s.pos, h.pos) > g.attackRangeOf(h) + 1))
    .sort((x, y) => flatDist(x.pos, h.pos) - flatDist(y.pos, h.pos))[0] ?? null;
}

/* No fight: take a monster when it's worth it, shop when that's worth the walk, else the most valuable point. */
function idle(g: Game, h: Hero, bot: BotState, foes: Hero[], hz: Hazard[]) {
  const hunted = bot.hunt != null ? g.monsters[bot.hunt] : undefined;
  if (h.order?.type === 'attack' && h.order.target !== hunted) h.order = null;
  if (g.taunted(h)) return;
  if (hunted && !huntStillOn(g, h, hunted, bot.huntSince ?? g.t)) {
    bot.hunt = null;
    if (h.order?.type === 'attack') h.order = null;
  }
  if (bot.hunt == null && !bot.shopping) {
    bot.hunt = monsterToHunt(g, h)?.id ?? null;
    bot.huntSince = g.t;
  }
  if (bot.hunt != null) { hunt(g, h, g.monsters[bot.hunt]!); return; }

  const [bx, bz] = LAYOUT.bases[h.team];
  const atFountain = flatDist(h.pos, { x: bx, z: bz }) < RULES.fountainRadius;
  // A trip home is for when it's also needed for hp — not with a capture nearly done or our point under attack
  // nearby; standing at the fountain anyway, it's free.
  const worthTrip = () => atFountain || (hpShare(h) < 0.6 && !captureFinishing(g, h, 3)
    && !g.points.some(p => pointThreatened(g, h, p) && flatDist(p.pos, h.pos) < 15));
  if (bot.shopping || (botShouldShop(g, h, foes, !!botRelicChoice(g, h)) && worthTrip())) {
    bot.shopping = true;
    if (atFountain) {
      botShop(g, h);
      const relic = botRelicChoice(g, h);
      if (relic) g.buyRelic(h, relic);
      bot.shopping = false;
    } else if (h.order?.type !== 'move') g.orderMove(h, bx, bz);
    return;
  }
  const gp = choosePoint(g, h, bot).pos;
  // Around the point by the hero's slot, point-reflected for team b like the map itself.
  const slot = h.id % 2, side = h.team === 'a' ? 1 : -1;
  const spot = shadowStir(g, h, safeGround(g, hz, { x: gp.x + side * Math.sin(slot * 2.3) * 1.2, z: gp.z + side * Math.cos(slot * 2.3) * 1.2 }, h.pos));
  if (flatDist(h.pos, spot) > 1.2 && (h.order?.type !== 'move' || !h.path.length || flatDist(h.path[h.path.length - 1]!, spot) > 2)) g.orderMove(h, spot.x, spot.z);
}

/* Together: wait near the den for the teammate, then both hit it. */
function hunt(g: Game, h: Hero, m: Monster) {
  const mate = mateOf(g, h)!, gather = GOALS.hunt.gather;
  const started = !!m.target && m.target.team === h.team;
  if (started || (flatDist(h.pos, m.pos) < gather && flatDist(mate.pos, m.pos) < gather + 2)) {
    if (h.order?.type !== 'attack' || h.order.target !== m) g.orderAttack(h, m);
    return;
  }
  const d = flatDist(h.pos, m.den), k = d > 0 ? (gather - 2) / d : 0;
  moveTo(g, h, m.den.x + (h.pos.x - m.den.x) * k, m.den.z + (h.pos.z - m.den.z) * k);
}

/* ---------------- Тень: Без отражения ---------------- */

/* Standing still on purpose (a silhouette within stillFor s, beyond every hit): on a point of ours against a
   lone enemy she isn't beating — she holds it and it keeps scoring — and, retreating as a silhouette already,
   while an enemy stands within reach of her. Measured: standing still to *become* one on the way out (2 s of
   free hits) doubled her deaths and cost half her wins. */
export function shadowHoldsStill(g: Game, h: Hero, bot: BotState): boolean {
  const foes = visibleEnemies(g, h).filter(e => flatDist(e.pos, h.pos) < BRAIN.fightRadius);
  if (!foes.length) return false;
  const ours = g.points.some(p => p.owner === h.team && flatDist(p.pos, h.pos) < LAYOUT.pointRadius);
  const mateNear = g.heroes.some(a => a !== h && a.team === h.team && a.alive && flatDist(a.pos, h.pos) < BRAIN.fightRadius);
  if (ours && foes.length === 1 && !mateNear && fightOdds(g, h).odds < 1) return true;
  return !!bot.retreat && g.silhouette(h) && foes.some(e => flatDist(e.pos, h.pos) < e.def.range + BRAIN.caught);
}

/* A silhouette adds nothing to a capture, so on a point not yet fully ours Тень never stands still long enough
   to turn into one: after `stirEvery` s she steps across the point instead of `spot` — unless she's being hit
   hard, when the silhouette's safety comes first. */
function shadowStir(g: Game, h: Hero, spot: Vec2): Vec2 {
  if (h.kind !== 'shadow' || g.t - h.stillSince < SHADOW.stirEvery) return spot;
  const p = g.points.find(q => flatDist(q.pos, h.pos) < LAYOUT.pointRadius);
  if (!p || (h.team === 'a' ? p.progress : -p.progress) >= 0.999) return spot;
  if (hitRecently(g, h, 1) && hpShare(h) < SHADOW.coverHp) return spot;
  let dx = p.pos.x - h.pos.x, dz = p.pos.z - h.pos.z, len = Math.hypot(dx, dz);
  if (len < 0.3) { dx = 1; dz = 0; len = 1; }
  return g.nav.nearestWalkable(p.pos.x + dx / len * SHADOW.stirStep, p.pos.z + dz / len * SHADOW.stirStep);
}

/* ---------------- skills ---------------- */

export function botSkills(g: Game, h: Hero, foe: Hero | undefined, leaving: boolean) {
  if (h.state === 'charging') {
    if (foe) aimCharge(g, h, foe.pos);
    if (h.charge && g.t - h.charge.t0 > 0.9) releaseCharge(g, h);
    return;
  }
  if (h.state === 'channel') { if (foe) aimCharge(g, h, foe.pos); return; }
  if (h.state !== 'normal') return;
  const kit: Record<SkillKey, any> = SKILLS[h.kind];
  for (const key of ['Q', 'W'] as SkillKey[]) {
    if (!g.canCast(h, key)) continue;
    const target = skillTarget(g, h, kit[key], foe, leaving);
    if (target === undefined || g.rng() < BRAIN.skillSkip) continue;
    if (castSkill(g, h, key, target)) return;
  }
}

/* Control is worth its cooldown when someone can follow it up, the target is getting away or nearly dead,
   it's on our teammate, or it peels a foe off us while we're in trouble. */
function ccWorth(g: Game, h: Hero, foe: Hero, pressed: boolean) {
  const d = flatDist(h.pos, foe.pos);
  const mateNear = g.heroes.some(a => a !== h && a.team === h.team && a.alive && flatDist(a.pos, foe.pos) < 6);
  const victim = attacking(foe);
  const onMate = !!victim && isHero(victim) && victim !== h && victim.team === h.team;
  const fleeing = !victim && d > h.def.range;
  return mateNear || onMate || fleeing || hpShare(foe) < 0.4 || (pressed && d < 5);
}

/* What to cast skill `sk` at right now: a hero, a ground point, null (no target needed) — or undefined for
   "not worth it". The per-skill `ai` hints (skills.ts) say which kind of situation each one is for. */
export function skillTarget(g: Game, h: Hero, sk: any, foe: Hero | undefined, leaving: boolean): Hero | Vec2 | null | undefined {
  const ai = sk.ai || {};
  const d = foe ? flatDist(h.pos, foe.pos) : Infinity;
  const enemies = visibleEnemies(g, h);
  const allies = g.heroes.filter(a => a.team === h.team && a.alive);
  const around = (r: number) => enemies.filter(e => flatDist(e.pos, h.pos) < r).length;
  const hit = hitRecently(g, h, 1);
  const focused = hit && (around(6) >= 2 || hpShare(h) < 0.4);
  const pressed = focused || leaving;
  // Nothing a couple of basic swings wouldn't do anyway: the cooldown is better kept.
  const wasted = !!foe && ehp(g, foe) <= h.def.dmg * 1.05 && d <= g.attackRangeOf(h) + 0.5;
  if (ai.engage && leaving) return undefined;
  if (ai.foe) {
    if (!foe || d < ai.foe[0] || d > ai.foe[1] || (ai.vsRanged && foe.def.melee)) return undefined;
    if (ai.cc ? !ccWorth(g, h, foe, pressed) : wasted) return undefined;
    return sk.targeting === 'instant' ? null : foe;
  }
  // Standing still to aim 0.9 s is no way to leave a fight.
  if (ai.charge) return foe && !leaving && d >= ai.charge[0] && d <= ai.charge[1] && !wasted ? foe : undefined;
  if (ai.shelter) {
    const struck = allies.filter(a => flatDist(a.pos, h.pos) < sk.r && hitRecently(g, a, 1));
    return struck.some(a => hpShare(a) < ai.shelter) || struck.length >= 2 ? null : undefined;
  }
  if (ai.near) {
    if (!foe || d >= ai.near) return undefined;
    if (ai.hpAbove && hpShare(h) <= ai.hpAbove) return undefined;
    if (ai.foeBelow && hpShare(foe) >= ai.foeBelow) return undefined;
    const r = sk.r ?? ai.near;
    if (ai.cc) return ccWorth(g, h, foe, pressed) || around(r) >= 2 ? null : undefined;
    if (ai.aoe || ai.peel) return (ai.aoe && around(r) >= ai.aoe) || (ai.peel && (pressed || (hit && d < 3))) ? null : undefined;
    if (ai.team) {
      const withMate = allies.some(a => a !== h && flatDist(a.pos, h.pos) < sk.r);
      return (withMate && (hit || d < h.def.range + 2)) || leaving || !attacking(foe) ? null : undefined;
    }
    return null;
  }
  if (ai.low) return foe && hit && d < 5 && (hpShare(h) < ai.low || around(5) >= 2) ? null : undefined;
  if (ai.flee) return foe && ((d < ai.flee && (hit || hpShare(h) < 0.6)) || (pressed && d < 5)) ? foe : undefined;
  if (ai.dodge) {
    if (!foe || d >= 3 || !hit || !(focused || hpShare(h) < ai.dodge)) return undefined;
    const sx = foe.pos.z - h.pos.z, sz = h.pos.x - foe.pos.x, len = Math.hypot(sx, sz) || 1;
    return { x: h.pos.x + sx / len * 3, z: h.pos.z + sz / len * 3 };
  }
  if (ai.stealth) return foe && d < ai.stealth ? null : undefined;
  // Голем из глины: one at a time — raised onto the foe near enough, else onto a point being taken.
  if (ai.golem) {
    if (golemOf(g, h) || leaving) return undefined;
    if (foe && d < ai.golem) return { x: foe.pos.x, z: foe.pos.z };
    const p = g.points.find(q => q.owner !== h.team && flatDist(q.pos, h.pos) < LAYOUT.pointRadius + 2);
    return p ? { x: p.pos.x, z: p.pos.z } : undefined;
  }
  // Связка: where a throw ties the most — two enemies to each other best, else an enemy to an anchor while a
  // teammate is on him or he is getting away. Spots tried: each enemy in reach, and the middle of each pair.
  if (ai.bind) {
    if (leaving || !foe) return undefined;
    const spots = enemies.filter(e => flatDist(e.pos, h.pos) <= sk.range + sk.r).map(e => ({ x: e.pos.x, z: e.pos.z }));
    for (const x of enemies) for (const y of enemies) if (x.id < y.id && flatDist(x.pos, y.pos) <= sk.r * 2) spots.push({ x: (x.pos.x + y.pos.x) / 2, z: (x.pos.z + y.pos.z) / 2 });
    let best: Vec2 | undefined, bestN = 0;
    for (const p of spots) {
      if (flatDist(p, h.pos) > sk.range) continue;
      const plan = bindPlan(g, h, p, sk.r);
      const n = plan.reduce((k, b) => k + ('id' in b.b && isHero(b.b) ? 2 : 1), 0);
      if (n > bestN) { best = p; bestN = n; }
    }
    return bestN >= 2 || (bestN === 1 && ccWorth(g, h, foe, pressed)) ? best : undefined;
  }
  if (ai.guard) {
    return foe && allies.some(a => a !== h && flatDist(a.pos, h.pos) < sk.r && hitRecently(g, a, 1) && hpShare(a) < ai.guard) ? null : undefined;
  }
  if (ai.hurt) {
    // The one taking damage now first, then the lowest — a heal on someone safe at 60% can wait.
    const want = allies.filter(a => flatDist(a.pos, h.pos) < sk.range && (hpShare(a) < ai.hurt || (hitRecently(g, a, 1) && hpShare(a) < 0.9)))
      .sort((a, b) => Number(hitRecently(g, b, 1)) - Number(hitRecently(g, a, 1)) || hpShare(a) - hpShare(b));
    return want[0];
  }
  if (ai.brawl) {
    if (!foe || d >= sk.range) return undefined;
    const mate = allies.find(a => a !== h && flatDist(a.pos, foe.pos) < 3);
    const p = mate ? mate.pos.clone().add(foe.pos).multiplyScalar(0.5) : foe.pos;
    return { x: p.x, z: p.z };
  }
  if (ai.burnFoe && foe && d < sk.range && (hpShare(foe) < 0.7 || enemies.filter(e => flatDist(e.pos, foe.pos) < sk.r).length >= 2)) {
    return { x: foe.pos.x, z: foe.pos.z };
  }
  if (ai.brush && !foe) {
    const pt = g.points.filter(p => p.owner !== h.team).sort((x, y) => flatDist(x.pos, h.pos) - flatDist(y.pos, h.pos))[0] || g.points[1];
    return pt && flatDist(pt.pos, h.pos) <= sk.range + 4 ? { x: pt.pos.x, z: pt.pos.z } : undefined;
  }
  return undefined;
}
